import { useCallback, useRef, useMemo } from "react";
import { useOverflowDetector } from "react-detectable-overflow";

/**
 * A wrapper around useOverflowDetector that adds a tolerance threshold.
 *
 * Behavior by view mode:
 * - preview/printPreview (user-facing): Pass through immediately, no filtering
 * - print/review (Puppeteer detection): Apply tolerance + RAF to filter false positives
 *
 * @param {Object} options
 * @param {Function} options.onChange - Callback when overflow state changes (receives boolean)
 * @param {number} options.tolerance - Minimum overflow in pixels to trigger in print mode (default: 5)
 * @returns {Object} { ref } - Ref to attach to the element to monitor
 */
export const useOverflowDetectorWithTolerance = ({ onChange, tolerance = 5 }) => {
  const containerRef = useRef(null);
  const lastOverflowState = useRef(false);
  const pendingCheck = useRef(null);

  // Check if we're in print/review mode (Puppeteer detection)
  // Only apply tolerance filtering in these modes
  const shouldFilter = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view");
    return view === "print" || view === "review";
  }, []);

  // Use the original hook to detect any overflow
  const { ref: originalRef } = useOverflowDetector({
    onChange: useCallback(
      (isOverflowing) => {
        // Clear any pending check
        if (pendingCheck.current) {
          cancelAnimationFrame(pendingCheck.current);
          pendingCheck.current = null;
        }

        // In preview/printPreview mode, pass through immediately without filtering
        if (!shouldFilter) {
          if (isOverflowing !== lastOverflowState.current) {
            lastOverflowState.current = isOverflowing;
            if (onChange) onChange(isOverflowing);
          }
          return;
        }

        // Print/review mode: Apply tolerance filtering for Puppeteer detection
        if (!isOverflowing) {
          if (lastOverflowState.current) {
            lastOverflowState.current = false;
            if (onChange) onChange(false);
          }
          return;
        }

        // When overflow is detected, delay measurement to allow layout to settle
        // This prevents false positives from transient overflow during render
        pendingCheck.current = requestAnimationFrame(() => {
          if (!containerRef.current) return;

          const elem = containerRef.current;
          const overflowAmount = elem.scrollHeight - elem.clientHeight;

          // Only trigger if overflow exceeds tolerance
          if (overflowAmount > tolerance) {
            if (!lastOverflowState.current) {
              lastOverflowState.current = true;
              if (onChange) onChange(true);
            }
          } else {
            // Below tolerance - treat as no overflow
            if (lastOverflowState.current) {
              lastOverflowState.current = false;
              if (onChange) onChange(false);
            }
          }
        });
      },
      [onChange, tolerance, shouldFilter]
    ),
  });

  // Combine refs - we need both the original ref for the library and our ref for measurement
  const setRefs = useCallback(
    (element) => {
      containerRef.current = element;
      // Set the original ref from useOverflowDetector
      if (typeof originalRef === "function") {
        originalRef(element);
      } else if (originalRef) {
        originalRef.current = element;
      }
    },
    [originalRef]
  );

  return { ref: setRefs };
};

export default useOverflowDetectorWithTolerance;
